"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import base from "./compositions.module.css";

type SceneProps = { children: ReactNode; className?: string };

/**
 * Fond de chaque composition. Pose `data-active="true"` quand l'illustration
 * doit jouer son geste : au survol de la carte qui la porte (le `li` le plus
 * proche) sur un appareil à souris, ou tant qu'elle est à l'écran sur un
 * appareil tactile (pas de survol possible). Les compositions
 * ne connaissent que `[data-active]` ; le mouvement lui-même est en CSS.
 */
export function Scene({ children, className }: SceneProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const scene = ref.current;
    const view = scene?.ownerDocument.defaultView;
    if (!scene || !view) return;
    if (view.matchMedia("(hover: hover)").matches) {
      const host = scene.closest("li") ?? scene.parentElement ?? scene;
      const on = (): void => setActive(true);
      const off = (): void => setActive(false);
      host.addEventListener("pointerenter", on);
      host.addEventListener("pointerleave", off);
      return () => {
        host.removeEventListener("pointerenter", on);
        host.removeEventListener("pointerleave", off);
      };
    }
    if (typeof IntersectionObserver === "undefined") return;
    // Tactile : actif tant que l'illustration est à l'écran, au repos dès qu'elle
    // en sort (les gestes en boucle ne tournent jamais hors champ).
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setActive(entry.isIntersecting);
      },
      { threshold: 0.5 },
    );
    observer.observe(scene);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={[base.scene, className].filter(Boolean).join(" ")} aria-hidden="true" data-active={active ? "true" : undefined}>
      {children}
    </div>
  );
}
